import { Fragment, ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudResourceEntity,
  accountDotColor,
} from '@internal/backstage-plugin-catalog-devops-common';
import { filterCloudFields } from '@internal/backstage-plugin-devops-react';
import { useCloudResourcesContext } from '../Providers';
import { ResourceTypeChip } from '../Chips';
import { CloudConsoleBadge } from '../CloudConsoleLink';
import { RegionChip } from '../RegionChip';
import { ProviderChip } from '../shared/ProviderChip';
import styles from '../resources-page.module.css';

const pascalToWords = (text: string): string => {
  return text.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const GROUP_FIELDS: Record<string, keyof CloudResourceEntity['spec']> = {
  Category: 'category',
  Service: 'service',
  Type: 'type',
  Region: 'region',
  Account: 'account',
  Provider: 'provider',
};

type SortState = { column: string; direction: 'asc' | 'desc' };

type ResourcesRow = {
  key: string;
  displayName: string;
  type: string;
  service: string;
  region: string;
  account: string;
  namespace: string;
  resourceRef: string;
  failedCount: number;
  failedTitle: string;
  entity: CloudResourceEntity;
  cloudFields: Record<string, unknown>;
};

type Column = {
  id: string;
  label: string;
  sortable?: boolean;
  sortValue?: (row: ResourcesRow) => string;
  render: (row: ResourcesRow) => ReactElement | null;
};

const PAGE_STEP = 50;

const sortArrow = (isSorted: boolean, direction: 'asc' | 'desc'): string => {
  if (!isSorted) return '↕';
  return direction === 'asc' ? '↑' : '↓';
};

/**
 * Resources table styled 1:1 after the design mockup: compact sortable
 * columns, in-table group header rows and failed-rule pills. With exactly
 * one type selected the type's cloud fields become dynamic columns (the old
 * behavior).
 */
export function ResourcesTable() {
  const {
    cloudResources,
    resourceExample,
    selectedTypes,
    selectedGroupBy,
    search,
    setSearch,
    selectedRegions,
    setSelectedRegions,
    selectedAccounts,
    setSelectedAccounts,
    selectedCategories,
    setSelectedCategories,
    unselectType,
    hasAccountFilter,
    selectResource,
    failedRulesByResource,
    allAccounts,
    accountProvider,
  } = useCloudResourcesContext();
  const navigate = useNavigate();

  const [sort, setSort] = useState<SortState>({
    column: 'displayName',
    direction: 'asc',
  });
  // Flat-mode window; grouped mode uses per-group caps instead.
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);
  // null = no interaction yet, the first (largest) group starts expanded.
  const [expandedGroups, setExpandedGroups] = useState<string[] | null>(null);
  const [groupVisibleCounts, setGroupVisibleCounts] = useState<
    Record<string, number>
  >({});

  const rows: ResourcesRow[] = useMemo(
    () =>
      cloudResources.map(resource => {
        const failedRules =
          failedRulesByResource[resource.metadata.name] ?? [];
        const bySeverity = new Map<string, number>();
        for (const rule of failedRules) {
          bySeverity.set(rule.severity, (bySeverity.get(rule.severity) ?? 0) + 1);
        }
        return {
          key: resource.metadata.name,
          displayName: resource.metadata.title ?? resource.metadata.name,
          type: resource.spec.type,
          service: resource.spec.service,
          region: resource.spec.region,
          account: resource.spec.account,
          namespace: resource.metadata.namespace ?? 'default',
          resourceRef: `/catalog/${
            resource.metadata.namespace ?? 'default'
          }/cloudresource/${resource.metadata.name}`,
          failedCount: failedRules.length,
          failedTitle: [...bySeverity.entries()]
            .map(([severity, count]) => `${severity}: ${count}`)
            .join('\n'),
          entity: resource,
          cloudFields: filterCloudFields(resource) as Record<string, unknown>,
        };
      }),
    [cloudResources, failedRulesByResource],
  );

  const singleType = selectedTypes.length === 1 ? selectedTypes[0] : undefined;

  const columns: Column[] = useMemo(() => {
    const nextColumns: Column[] = [
      {
        id: 'displayName',
        label: 'Resource',
        sortable: true,
        sortValue: row => row.displayName.toLowerCase(),
        render: row => (
          <>
            <a
              className={styles.resName}
              href={row.resourceRef}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}
            >
              {row.displayName}
            </a>
            <CloudConsoleBadge resource={row.entity} />
          </>
        ),
      },
    ];

    if (!singleType) {
      nextColumns.push({
        id: 'type',
        label: 'Type',
        sortable: true,
        sortValue: row => row.type.toLowerCase(),
        render: row =>
          row.type ? (
            <ResourceTypeChip type={row.type} service={row.service} />
          ) : null,
      });
    }

    nextColumns.push(
      {
        id: 'region',
        label: 'Region',
        sortable: true,
        sortValue: row => row.region.toLowerCase(),
        render: row => <RegionChip region={row.region} />,
      },
      {
        id: 'failedRules',
        label: 'Failed rules',
        render: row =>
          row.failedCount > 0 ? (
            <span
              className={`${styles.pill} ${styles.pillFailed}`}
              title={row.failedTitle}
            >
              {row.failedCount} {row.failedCount === 1 ? 'rule' : 'rules'}
            </span>
          ) : (
            <span
              className={`${styles.pill} ${styles.pillPassed}`}
              title="No failed rules"
            >
              ✓
            </span>
          ),
      },
    );

    if (hasAccountFilter) {
      nextColumns.push({
        id: 'account',
        label: 'Account',
        sortable: true,
        sortValue: row => row.account.toLowerCase(),
        render: row => {
          const provider = (accountProvider[row.account] ?? '').toUpperCase();
          const dotColor = accountDotColor(
            Math.max(0, allAccounts.indexOf(row.account)),
          );
          return (
            <a
              href={`/catalog/default/account/${row.account}/cloud-resources`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}
              className={styles.accCell}
              title={provider ? `Open ${row.account} (${provider})` : row.account}
            >
              <span className={styles.accDot} style={{ background: dotColor }} />
              <ProviderChip provider={provider} />
              {row.account}
            </a>
          );
        },
      });
    }

    // With exactly one type selected the type's cloud fields become columns
    // (EC2 Images get their devices count, like the old grid did).
    if (singleType && resourceExample) {
      const cloudFields = filterCloudFields(resourceExample);
      for (const field of Object.keys(cloudFields)) {
        nextColumns.push({
          id: field,
          label: pascalToWords(field),
          render: row => <>{String(row.cloudFields[field] ?? '')}</>,
        });
      }
      if (resourceExample.spec.type === 'EC2 Image') {
        nextColumns.push({
          id: 'devices',
          label: 'Devices',
          render: row => {
            const devices = row.cloudFields.devices;
            return <>{Array.isArray(devices) ? devices.length : ''}</>;
          },
        });
      }
    }

    return nextColumns;
  }, [singleType, resourceExample, hasAccountFilter, accountProvider, allAccounts]);

  const sortedRows = useMemo(() => {
    const column = columns.find(candidate => candidate.id === sort.column);
    const direction = sort.direction === 'desc' ? -1 : 1;
    const sorted = [...rows];
    if (column?.sortValue) {
      sorted.sort(
        (a, b) => column.sortValue!(a).localeCompare(column.sortValue!(b)) * direction,
      );
    }
    return sorted;
  }, [rows, columns, sort]);

  const handleSort = (column: Column) => {
    if (!column.sortable) return;
    setVisibleCount(PAGE_STEP);
    setSort(current =>
      current.column === column.id
        ? { column: column.id, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { column: column.id, direction: 'asc' },
    );
  };

  const clearAllFilters = () => {
    setSearch('');
    setSelectedCategories([]);
    setSelectedRegions([]);
    setSelectedAccounts([]);
    selectedTypes.forEach(type => unselectType(type));
    setVisibleCount(PAGE_STEP);
  };

  const emptyState = (
    <div className={styles.empty}>
      <b>No resources match the current filters</b>
      Try widening the filters.
      <br />
      <button type="button" onClick={clearAllFilters}>
        Clear all filters
      </button>
    </div>
  );

  // Groups over the FULL sorted set so header counts are always honest
  // (how many resources and failed checks the group really has).
  const groupedSections = useMemo(() => {
    const groupField = GROUP_FIELDS[selectedGroupBy[0]];
    if (!groupField) return null;

    const groups = new Map<string, ResourcesRow[]>();
    for (const row of sortedRows) {
      const key = String(row.entity.spec[groupField] || '—');
      const groupRows = groups.get(key);
      if (groupRows) {
        groupRows.push(row);
      } else {
        groups.set(key, [row]);
      }
    }

    return [...groups.entries()].sort(
      ([, a], [, b]) =>
        b.length - a.length || a[0].displayName.localeCompare(b[0].displayName),
    );
  }, [sortedRows, selectedGroupBy]);

  const isGroupExpanded = (groupName: string) => {
    if (expandedGroups) {
      return expandedGroups.includes(groupName);
    }
    return groupedSections?.[0]?.[0] === groupName;
  };

  const toggleGroup = (groupName: string) => {
    const current =
      expandedGroups ??
      (groupedSections?.[0]?.[0] === groupName ? [groupedSections[0][0]] : []);
    setExpandedGroups(
      current.includes(groupName)
        ? current.filter(name => name !== groupName)
        : [...current, groupName],
    );
  };

  const showMoreInGroup = (groupName: string) => {
    setGroupVisibleCounts(counts => ({
      ...counts,
      [groupName]: (counts[groupName] ?? PAGE_STEP) + PAGE_STEP,
    }));
  };

  // Project mode: a row drills into the owning account's resources tab;
  // account mode keeps the quick-view drawer.
  const onRowClick = (row: ResourcesRow) => {
    if (hasAccountFilter) {
      navigate(`/catalog/default/account/${row.account}/cloud-resources`);
      return;
    }
    selectResource(row.entity);
  };

  const renderBody = () => {
    if (!groupedSections) {
      return sortedRows.slice(0, visibleCount).map(row => (
        <tr
          key={row.key}
          className={styles.row}
          onClick={() => onRowClick(row)}
        >
          {columns.map(column => (
            <td key={column.id}>{column.render(row)}</td>
          ))}
        </tr>
      ));
    }

    const output: ReactElement[] = [];

    for (const [groupName, groupRows] of groupedSections) {
      const failedCount = groupRows.reduce(
        (sum, row) => sum + row.failedCount,
        0,
      );
      const expanded = isGroupExpanded(groupName);
      const visibleInGroup = groupVisibleCounts[groupName] ?? PAGE_STEP;
      const shownRows = expanded
        ? groupRows.slice(0, visibleInGroup)
        : [];
      const hiddenInGroup = groupRows.length - shownRows.length;

      output.push(
        <Fragment key={`group-${groupName}`}>
          <tr className={styles.groupRow}>
            <td colSpan={columns.length}>
              <button
                type="button"
                className={styles.groupToggle}
                aria-expanded={expanded}
                onClick={() => toggleGroup(groupName)}
              >
                <span className={styles.chevron} aria-hidden="true">
                  {expanded ? '▾' : '▸'}
                </span>
                {groupName}
                <span className={styles.groupCount}>
                  {groupRows.length}{' '}
                  {groupRows.length === 1 ? 'resource' : 'resources'} ·{' '}
                  {failedCount} failed{' '}
                  {failedCount === 1 ? 'check' : 'checks'}
                </span>
              </button>
            </td>
          </tr>
          {shownRows.map(row => (
            <tr
              key={row.key}
              className={styles.row}
              onClick={() => onRowClick(row)}
            >
              {columns.map(column => (
                <td key={column.id}>{column.render(row)}</td>
              ))}
            </tr>
          ))}
          {expanded && hiddenInGroup > 0 && (
            <tr className={styles.showMoreRow}>
              <td colSpan={columns.length}>
                <button
                  type="button"
                  className={styles.showMore}
                  onClick={() => showMoreInGroup(groupName)}
                >
                  Show more ({hiddenInGroup} remaining)
                </button>
              </td>
            </tr>
          )}
        </Fragment>,
      );
    }

    return output;
  };

  if (!rows.length) {
    return (
      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.id}>{column.label}</th>
              ))}
            </tr>
          </thead>
        </table>
        {search ||
        selectedCategories.length ||
        selectedRegions.length ||
        selectedAccounts.length ||
        selectedTypes.length ? (
          emptyState
        ) : (
          <div className={styles.empty}>
            <b>No resources</b>
            This {hasAccountFilter ? 'project' : 'account'} has no inventoried
            resources yet.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.tableCard}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map(column => {
              const isSorted = sort.column === column.id;
              return (
                <th
                  key={column.id}
                  className={
                    (column.sortable ? styles.sortable : '') +
                    (isSorted ? ` ${styles.sorted}` : '')
                  }
                  onClick={() => handleSort(column)}
                >
                  {column.label}
                  {column.sortable && (
                    <span className={styles.sArrow}>
                      {sortArrow(isSorted, sort.direction)}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{renderBody()}</tbody>
      </table>
      {!groupedSections && sortedRows.length > visibleCount && (
        <div className={styles.tableFooter}>
          <button
            type="button"
            className={styles.showMore}
            onClick={() => setVisibleCount(count => count + PAGE_STEP)}
          >
            Show more ({sortedRows.length - visibleCount} remaining)
          </button>
        </div>
      )}
    </div>
  );
}
